/**
 * School Mobile App Onboarding Config API Contract
 *
 * GET  /v1/schools/:school_id/mobile-app/onboarding-config
 * PUT  /v1/schools/:school_id/mobile-app/onboarding-config
 * GET  /v1/schools/:school_id/mobile-app/onboarding-config/versions
 * POST /v1/schools/:school_id/mobile-app/onboarding-config/versions/:versionId/restore
 *
 * The school admin's lane onto the same `MobileApp.onboardingConfig` the
 * super-admin editor writes (plan 2026-08-12-001 U2, U11). The app row is
 * resolved from the school and never named by the caller, and the write runs
 * through the same service core as `PUT /v1/mobile-apps/:id/onboarding-config`,
 * so the two lanes cannot disagree about what a valid config is (KTD1).
 *
 * What the school lane CANNOT do, by contract rather than by UI (KTD3, R3a):
 * move the carousel switch or the login layout-tuning numbers. Those fields are
 * absent from the request schema, so a body that carries them fails `.strict()`,
 * and the backend copies the stored values into every config it writes, on save
 * and on restore.
 *
 * Every write names the config it was loaded from (`expectedFingerprint`, KTD4).
 * A save or restore made against a config someone else has since changed is
 * refused with `409 CONFLICT` and writes nothing, rather than silently
 * overwriting their text. The super-admin lane keeps last-writer-wins.
 */

import { z } from 'zod'
import {
  MobileAppOnboardingConfigSchema,
  MobileAppOnboardingSlideSchema,
  type MobileAppOnboardingConfig
} from '../../schemas/entities/mobile-app-onboarding'

/**
 * The onboarding-config fields a school can never set: the carousel switch and
 * the login layout-tuning numbers.
 *
 * The switch is a build-lane decision whose owner is `brandingConfig`
 * (`deriveOnboardingEnabled` prefers it). The layout numbers are registry-served
 * to a public screen with no rebuild gate between a save and a signed-out
 * family, and a bad value pushes the sign-in button off the frame. So the school
 * lane pins all of them to what is stored, and the school editor renders none
 * of them.
 */
export const SCHOOL_LOCKED_ONBOARDING_FIELDS = [
  'onboardingEnabled',
  'logoWidth',
  'logoHeight',
  'logoMarginTop',
  'logoAbsolutePosition',
  'buttonMarginBottom',
  'copyToActionsGap',
  'welcomeTextFontSize'
] as const satisfies readonly (keyof MobileAppOnboardingConfig)[]
export type SchoolLockedOnboardingField = (typeof SCHOOL_LOCKED_ONBOARDING_FIELDS)[number]

// `satisfies Record<…, true>` makes this mask and the list above fail to compile
// the moment they disagree, in either direction.
const SCHOOL_LOCKED_FIELD_MASK = {
  onboardingEnabled: true,
  logoWidth: true,
  logoHeight: true,
  logoMarginTop: true,
  logoAbsolutePosition: true,
  buttonMarginBottom: true,
  copyToActionsGap: true,
  welcomeTextFontSize: true
} as const satisfies Record<SchoolLockedOnboardingField, true>

/**
 * The copy a school admin sends: the stored schema without the locked fields,
 * and at least one slide. The stored schema allows zero slides because a
 * school can have its carousel switched off; the school editor always edits at
 * least one slide, and a zero-slide save from it is a mistake, not an intent.
 */
export const SchoolOnboardingCopySchema = MobileAppOnboardingConfigSchema.omit(SCHOOL_LOCKED_FIELD_MASK)
  .extend({ slides: z.array(MobileAppOnboardingSlideSchema).min(1).max(10) })
  .strict()
export type SchoolOnboardingCopy = z.infer<typeof SchoolOnboardingCopySchema>

/** The sha256 of the stored config's canonical JSON, as the GET returned it. */
const ExpectedFingerprintSchema = z.string().regex(/^[0-9a-f]{64}$/, 'expectedFingerprint must be a sha256 hex digest')

/**
 * GET and PUT response.
 *
 * `config` is the stored config in full, locked fields included (they are what
 * the public pre-login endpoint serves anyway); the editor strips them before a
 * save. `fingerprint` is what the next save or restore must send back, and is
 * null exactly when `config` is: nothing is stored, or what is stored no longer
 * parses. Both of those are a blocked state for the school editor, not an empty
 * one, so a school save against them is refused (`409 NO_STORED_CONFIG`).
 *
 * `onboardingEnabled` is the DERIVED carousel state (`brandingConfig` first,
 * then the copy's own mirror), present even when `config` is null, so the tab
 * can say "School Journey manages this setting" without reading the switch out
 * of the copy it does not own.
 */
export const SchoolOnboardingConfigResponseSchema = z.object({
  config: MobileAppOnboardingConfigSchema.nullable(),
  fingerprint: z.string().nullable(),
  onboardingEnabled: z.boolean().nullable()
})
export type SchoolOnboardingConfigResponse = z.infer<typeof SchoolOnboardingConfigResponseSchema>

/**
 * PUT body. `expectedFingerprint` is REQUIRED: a school save that cannot say
 * which config it was loaded from is refused before anything is written.
 *
 * The config rides in its own key rather than flat beside the fingerprint so
 * the copy object stays exactly the shape the device renders.
 */
export const UpdateSchoolOnboardingConfigRequestSchema = z
  .object({
    expectedFingerprint: ExpectedFingerprintSchema,
    config: SchoolOnboardingCopySchema
  })
  .strict()
export type UpdateSchoolOnboardingConfigRequest = z.infer<typeof UpdateSchoolOnboardingConfigRequestSchema>

/**
 * One entry in the school's recent-changes list: a TRANSITION, not a row
 * (KTD13).
 *
 * A version row holds the config that was displaced and who displaced it, so
 * the entry reads "`actorLabel` changed `changedFields` at `changedAt`", where
 * the fields are the difference between this row's config and the next newer
 * one (the live config, for the newest entry). `config` is the text a restore
 * of this entry brings back, which is how it was BEFORE this change.
 *
 * `changedFields` names top-level config keys, plus `slides[i]` for a slide
 * added or removed and `slides[i].TITLE` / `slides[i].DESCRIPTION` for a slide
 * edited in place (`i` zero-based). Locked fields appear by name when they moved;
 * the school surface is expected to collapse them into one phrase.
 *
 * `actorLabel` is a person's name only when the change came through the school
 * dashboard AND that person has a seat at the school; everyone else, staff and
 * automation alike, is "School Journey team". `laneLabel` is the lane in plain
 * words. `restorable` is false for an entry the school lane could not have
 * saved: the "nothing stored" state, or a config with no slides.
 */
export const SchoolOnboardingConfigChangeSchema = z.object({
  id: z.string(),
  changedAt: z.string().datetime(),
  actorLabel: z.string(),
  laneLabel: z.string(),
  changedFields: z.array(z.string()),
  config: MobileAppOnboardingConfigSchema.nullable(),
  restorable: z.boolean()
})
export type SchoolOnboardingConfigChange = z.infer<typeof SchoolOnboardingConfigChangeSchema>

/** GET versions response: newest first; an app with no history returns an empty list. */
export const ListSchoolOnboardingConfigVersionsResponseSchema = z.object({
  versions: z.array(SchoolOnboardingConfigChangeSchema)
})
export type ListSchoolOnboardingConfigVersionsResponse = z.infer<
  typeof ListSchoolOnboardingConfigVersionsResponseSchema
>

/** POST restore body. Required for the same reason as on the PUT. */
export const RestoreSchoolOnboardingConfigVersionRequestSchema = z
  .object({ expectedFingerprint: ExpectedFingerprintSchema })
  .strict()
export type RestoreSchoolOnboardingConfigVersionRequest = z.infer<
  typeof RestoreSchoolOnboardingConfigVersionRequestSchema
>

/** POST restore response: the now-live config and the fingerprint the next write must send. */
export const RestoreSchoolOnboardingConfigVersionResponseSchema = SchoolOnboardingConfigResponseSchema.extend({
  restoredVersionId: z.string()
})
export type RestoreSchoolOnboardingConfigVersionResponse = z.infer<
  typeof RestoreSchoolOnboardingConfigVersionResponseSchema
>
